// MCU READ WRITE — gabungan dua jalur pemrograman MCU:
// MCU READ WRITE — panel MPC56xx (bridge lokal) + Renesas R8C/M16C (serial boot mode).
//  • Renesas R8C/M16C lewat serial boot mode (TRMD Engine)
// Chrome visual & frame melayang mengikuti Honda Flashtools.
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Cpu, MemoryStick, X, Microchip, Cable } from "lucide-react";
import { RenesasFlashTool } from "../flashtools/components/RenesasFlashTool";
import { Rh850FlashTool } from "../flashtools/components/Rh850FlashTool";
import { McuPinoutTab } from "../flashtools/components/McuPinoutTab";
import Mpc5602dFlash from "./Mpc5602dFlash";

const TABS = [
  { id: "jlink", label: "MPC56XX", icon: Cpu },
  { id: "renesas", label: "R8C/M16C", icon: MemoryStick },
  { id: "rh850", label: "RH850", icon: Microchip },
  { id: "pinout", label: "PIN OUT", icon: Cable },
];

const BASE_W = 1280, BASE_H = 860;
const IS_ANDROID = typeof navigator !== "undefined" && /Android|TiroTunerApp/i.test(navigator.userAgent || "");
const calcFit = () => {
  const vw = window.innerWidth, vh = window.innerHeight;
  const narrow = vw < 820 && vh > vw;
  const scale = narrow ? 1 : IS_ANDROID ? Math.min(vw / BASE_W, vh / BASE_H) : Math.min(1.1, (vw * 0.88) / BASE_W, (vh * 0.9) / BASE_H);
  return { vw, vh, narrow, scale };
};

const PanelHead = ({ icon, title, onClose }) => (
  <div className="tt-dark relative flex items-stretch overflow-hidden bg-[#3a0a0d] border-b border-red-800/60"
    style={{ backgroundImage: "url(/trmd-head.webp)", backgroundSize: "100% 100%" }}>
    <div className="flex items-center gap-3 pl-5 pr-14 py-2 bg-gradient-to-b from-[#ef2b2b] to-[#c51717]"
      style={{ clipPath: "polygon(0 0, 100% 0, calc(100% - 24px) 100%, 0 100%)" }}>
      <span className="text-white flex items-center">{icon}</span>
      <h2 className="text-white font-black tracking-wide uppercase text-[14px] sm:text-[19px]">{title}</h2>
    </div>
    <div className="w-3 -ml-6 bg-red-500/50" style={{ clipPath: "polygon(70% 0, 100% 0, 30% 100%, 0 100%)" }} />
    <button onClick={onClose} data-testid="mcu-close"
      className="ml-auto px-4 text-white/80 hover:text-white hover:bg-white/10 inline-flex items-center">
      <X size={18} />
    </button>
  </div>
);

const Panel = ({ onClose }) => {
  const [tab, setTab] = useState("jlink");
  const [locked, setLocked] = useState({ jlink: false, renesas: false, rh850: false });
  const anyLocked = locked.jlink || locked.renesas || locked.rh850;
  const onJlinkBusy = useCallback((b) => setLocked((p) => (p.jlink === b ? p : { ...p, jlink: b })), []);
  const onRenesasBusy = useCallback((b) => setLocked((p) => (p.renesas === b ? p : { ...p, renesas: b })), []);
  const onRh850Busy = useCallback((b) => setLocked((p) => (p.rh850 === b ? p : { ...p, rh850: b })), []);
  const active = TABS.find((t) => t.id === tab);
  return (
    <div className="h-full flex flex-col bg-[#0F1015] text-zinc-100" data-testid="mcu-read-write-page">
      <div className="relative shrink-0 overflow-hidden bg-[#0a0b0e] h-12 sm:h-[64px]">
        <img src="/trmd-banner.webp" alt="" className="h-full w-full object-cover object-left select-none pointer-events-none" draggable={false} />
        <img src="/trmd-logo.webp" alt="TRMD" draggable={false}
          className="absolute right-5 top-1/2 -translate-y-1/2 w-auto object-contain select-none pointer-events-none max-w-[32%] h-8 sm:h-[46px]" />
      </div>

      <div className="flex-1 min-h-0 p-2 sm:p-3">
        <div className="h-full rounded-md border border-red-900/40 bg-[#0a0b0e] overflow-hidden flex flex-col">
          <PanelHead icon={active?.icon ? React.createElement(active.icon, { size: 18 }) : null}
            title={`MCU READ WRITE · ${active?.label || ""}`} onClose={onClose} />
          <div className="flex-1 min-h-0">
            <div className={tab === "jlink" ? "h-full overflow-y-auto" : "hidden"}><Mpc5602dFlash embedded onBusyChange={onJlinkBusy} /></div>
            <div className={tab === "renesas" ? "h-full overflow-y-auto p-2 sm:p-3" : "hidden"} data-testid="mcu-renesas-panel">
              <RenesasFlashTool onBusyChange={onRenesasBusy} />
            </div>
            <div className={tab === "rh850" ? "h-full overflow-y-auto p-2 sm:p-3" : "hidden"} data-testid="mcu-rh850-panel">
              <Rh850FlashTool onBusyChange={onRh850Busy} />
            </div>
            <div className={tab === "pinout" ? "h-full min-h-0 overflow-y-auto" : "hidden"} data-testid="mcu-pinout-panel">
              <McuPinoutTab />
            </div>
          </div>
        </div>
      </div>

      <div className="shrink-0 border-t border-red-900/40 bg-[#0a0b0e] px-2 sm:px-3 py-2">
        <div className="tt-dark grid grid-cols-4 gap-0 rounded-lg overflow-hidden border border-red-900/50">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} disabled={anyLocked && tab !== t.id} data-testid={`mcu-tab-${t.id}`}
                title={anyLocked && tab !== t.id ? "Tab locked: a device is still connected or an operation is running" : undefined}
                className={`h-11 inline-flex items-center justify-center gap-2 font-black uppercase tracking-wide text-[11px] sm:text-[14px] transition-colors ${
                  tab === t.id ? "bg-gradient-to-b from-[#ef2b2b] to-[#c51717] text-white" : "bg-[#15171d] text-zinc-400 hover:text-zinc-100 hover:bg-[#1c1f26]"
                } ${anyLocked && tab !== t.id ? "opacity-40 cursor-not-allowed hover:bg-[#15171d] hover:text-zinc-400" : ""}`}>
                <Icon size={17} className="shrink-0" />
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default function McuReadWrite() {
  const navigate = useNavigate();
  const [fit, setFit] = useState(calcFit);
  useEffect(() => {
    const on = () => setFit(calcFit());
    window.addEventListener("resize", on);
    window.addEventListener("orientationchange", on);
    return () => { window.removeEventListener("resize", on); window.removeEventListener("orientationchange", on); };
  }, []);

  const full = IS_ANDROID && !fit.narrow;
  const style = fit.narrow ? undefined : full ? { width: fit.vw / fit.scale, height: fit.vh / fit.scale, zoom: fit.scale } : { width: BASE_W, height: BASE_H, zoom: fit.scale };
  const box = fit.narrow
    ? "w-screen min-h-[100dvh] rounded-none"
    : full ? "rounded-none overflow-hidden border-0"
    : "rounded-xl overflow-hidden ring-1 ring-red-600/80 shadow-[0_0_10px_rgba(239,43,43,0.55),0_30px_80px_rgba(0,0,0,0.8)]";

  return (
    <div className={`fixed inset-0 z-[200] ${full ? "bg-[#0F1015]" : "bg-black/80 backdrop-blur-sm"} ${fit.narrow ? "overflow-y-auto p-0" : "flex items-center justify-center"}`}
      data-testid="mcu-page-frame">
      <div style={style} className={`relative border border-white/10 bg-[#0F1015] shadow-2xl ${box}`}>
        <Panel onClose={() => navigate("/")} />
      </div>
    </div>
  );
}
